import { useState } from "react";
import { Check, CreditCard, Pencil, TrendingUp, Users } from "lucide-react";
import { MasterShell } from "@/shared/layouts/MasterShell.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { Input } from "@/shared/ui/input.jsx";
import { Label } from "@/shared/ui/label.jsx";
import { Switch } from "@/shared/ui/switch.jsx";
import { LoadingState, Spinner } from "@/shared/ui/spinner.jsx";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/shared/ui/dialog.jsx";
import { formatCurrency, formatNumber } from "@/shared/lib/format.js";
import { useAuth } from "@/features/auth/auth-context.jsx";
import { canWriteMaster } from "@/shared/lib/roles.js";
import { StatCard } from "./components/MasterUi.jsx";
import { useMasterClients, useMasterPlans, useSavePlan } from "./use-master.js";

/**
 * Planos e assinaturas.
 *
 * Cada plano mostra quantos clientes o assinam e quanto ele gera de receita
 * — números vindos da carteira real, não de um mock.
 */
export function SubscriptionsPage() {
  const { user } = useAuth();
  const podeEscrever = canWriteMaster(user);

  const { data: planos = [], isLoading } = useMasterPlans();
  const { data: clientes = [] } = useMasterClients();

  const [emEdicao, setEmEdicao] = useState(null);

  if (isLoading) {
    return (
      <MasterShell title="Assinaturas" subtitle="Planos comercializados">
        <LoadingState message="Carregando planos..." />
      </MasterShell>
    );
  }

  /** Assinantes e receita de cada plano, calculados sobre a carteira. */
  const porPlano = planos.map((plano) => {
    const assinantes = clientes.filter((c) => c.planId === plano.id);
    const ativos = assinantes.filter((c) => c.status === "ativo");
    return {
      ...plano,
      assinantes: assinantes.length,
      ativos: ativos.length,
      receita: ativos.reduce((soma, c) => soma + c.monthlyValue, 0),
    };
  });

  const mrrTotal = porPlano.reduce((soma, p) => soma + p.receita, 0);
  const totalAtivos = porPlano.reduce((soma, p) => soma + p.ativos, 0);
  const ticketMedio = totalAtivos > 0 ? mrrTotal / totalAtivos : 0;

  return (
    <MasterShell title="Assinaturas" subtitle="Planos, assinantes e receita recorrente">
      <div className="space-y-6 p-6">
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard label="Receita recorrente" value={formatCurrency(mrrTotal)} icon={TrendingUp} accent="success" />
          <StatCard label="Assinaturas ativas" value={totalAtivos} icon={Users} accent="primary" />
          <StatCard label="Ticket médio" value={formatCurrency(ticketMedio)} icon={CreditCard} accent="chart-2" />
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {porPlano.map((plano) => (
            <Card key={plano.id} className={plano.active ? "" : "opacity-60"}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="font-display text-lg">{plano.name}</CardTitle>
                    <p className="mt-1 font-display text-2xl font-semibold">
                      {formatCurrency(plano.price)}
                      <span className="text-sm font-normal text-muted-foreground">/mês</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant={plano.active ? "success" : "outline"} className="text-[10px]">
                      {plano.active ? "Ativo" : "Inativo"}
                    </Badge>
                    {podeEscrever && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setEmEdicao(plano)}
                        aria-label={`Editar plano ${plano.name}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3 rounded-lg bg-muted/40 p-3 text-center">
                  <div>
                    <p className="font-display text-xl font-semibold">{plano.ativos}</p>
                    <p className="text-[11px] text-muted-foreground">assinantes ativos</p>
                  </div>
                  <div>
                    <p className="font-display text-xl font-semibold">
                      {formatCurrency(plano.receita)}
                    </p>
                    <p className="text-[11px] text-muted-foreground">receita/mês</p>
                  </div>
                </div>

                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                    {plano.limitUsers > 0 ? `Até ${plano.limitUsers} usuários` : "Usuários ilimitados"}
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                    {plano.limitMessages > 0
                      ? `${formatNumber(plano.limitMessages)} mensagens/mês`
                      : "Mensagens ilimitadas"}
                  </li>
                  {plano.modules.map((modulo) => (
                    <li key={modulo} className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                      {modulo}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <PlanDialog plano={emEdicao} onClose={() => setEmEdicao(null)} />
    </MasterShell>
  );
}

function PlanDialog({ plano, onClose }) {
  return (
    <Dialog open={Boolean(plano)} onOpenChange={(estado) => !estado && onClose()}>
      <DialogContent>
        {plano && <FormularioPlano key={plano.id} plano={plano} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

function FormularioPlano({ plano, onClose }) {
  const salvar = useSavePlan();
  const [form, setForm] = useState(() => ({
    name: plano.name,
    price: String(plano.price),
    limitUsers: String(plano.limitUsers),
    limitMessages: String(plano.limitMessages),
    modules: plano.modules.join(", "),
    active: plano.active,
  }));

  const set = (campo) => (valor) => setForm((atual) => ({ ...atual, [campo]: valor }));

  function submeter(evento) {
    evento.preventDefault();
    salvar.mutate(
      {
        id: plano.id,
        name: form.name,
        price: Number(form.price) || 0,
        limitUsers: Number(form.limitUsers) || 0,
        limitMessages: Number(form.limitMessages) || 0,
        modules: form.modules
          .split(",")
          .map((m) => m.trim())
          .filter(Boolean),
        active: form.active,
      },
      { onSuccess: onClose },
    );
  }

  return (
    <form onSubmit={submeter}>
      <DialogHeader>
        <DialogTitle>Editar plano {plano.name}</DialogTitle>
        <DialogDescription>
          Alterar o preço não muda a mensalidade dos contratos já assinados.
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-4 py-4 sm:grid-cols-2">
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="plan-name">Nome</Label>
          <Input id="plan-name" required value={form.name} onChange={(e) => set("name")(e.target.value)} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="plan-price">Preço mensal (R$)</Label>
          <Input
            id="plan-price"
            type="number"
            min="0"
            step="0.01"
            value={form.price}
            onChange={(e) => set("price")(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="plan-users">Limite de usuários</Label>
          <Input
            id="plan-users"
            type="number"
            min="0"
            value={form.limitUsers}
            onChange={(e) => set("limitUsers")(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">0 = ilimitado</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="plan-messages">Limite de mensagens/mês</Label>
          <Input
            id="plan-messages"
            type="number"
            min="0"
            value={form.limitMessages}
            onChange={(e) => set("limitMessages")(e.target.value)}
          />
        </div>

        <div className="flex items-end gap-3 pb-1">
          <Switch
            id="plan-active"
            checked={form.active}
            onCheckedChange={set("active")}
            aria-label="Plano ativo"
          />
          <Label htmlFor="plan-active">Plano ativo</Label>
        </div>

        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="plan-modules">Módulos incluídos</Label>
          <Input
            id="plan-modules"
            value={form.modules}
            onChange={(e) => set("modules")(e.target.value)}
            placeholder="CRM, WhatsApp, Agenda"
          />
          <p className="text-xs text-muted-foreground">Separe por vírgula.</p>
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button type="submit" disabled={salvar.isPending}>
          {salvar.isPending ? <Spinner /> : null}
          Salvar plano
        </Button>
      </DialogFooter>
    </form>
  );
}
